import { iconIdMapping } from '@arcade/web/icons/react'
import {
  BaseCell,
  BaseCellProps,
  CheckboxCell,
  InteractiveCell,
  PushCell,
  RadioCell,
  Text,
} from '@cash-design-system/react'
import type {
  CellDefault,
  CellDefaultCellDefaultIcon,
} from '@cash-web/protos/squareup/cash/ui/arcade/elements/CellDefault.pb'
import logger from '@cash-web/shared/util-logger'

import { ArcadeAccessoryLabeledPushFromProto } from '../ArcadeAccessoryLabeledPushFromProto/ArcadeAccessoryLabeledPushFromProto'
import { ArcadeCellLabelAccessoryWrapper } from '../ArcadeCellLabelAccessoryWrapper/ArcadeCellLabelAccessoryWrapper'
import { TintedArcadeCellIcon } from '../TintedArcadeCellIcon/TintedArcadeCellIcon'

const getAssetIcon = ({
  protoProps,
}: {
  protoProps: CellDefaultCellDefaultIcon
}): BaseCellProps['asset'] | undefined => {
  if (protoProps?.tinted_icon) {
    return { avatar: <TintedArcadeCellIcon tintedIcon={protoProps.tinted_icon} /> }
  } else if (protoProps?.icon) {
    const iconArcadeId = protoProps.icon.arcade_id
    const iconInfo = iconArcadeId ? iconIdMapping[iconArcadeId] : undefined
    if (!iconInfo) {
      // If you run into this error it either means we need to update our mapping, or the server
      // accidentally provided an invalid arcade id.
      logger.warn(new Error(`ArcadeCellIconFromProto: iconArcadeId is missing from mapping: ${iconArcadeId}`))
      return undefined
    }
    return { Icon: iconInfo.Icon }
  }

  logger.warn(new Error('ArcadeCellIconFromProto: no icon or tinted_icon found in protoProps'))
  return undefined
}

export function ArcadeCellFromProto({
  protoProps,
  onClick,
  selectionGroupId,
  initialSelection,
}: {
  protoProps: CellDefault
  onClick: () => void
  selectionGroupId?: string
  initialSelection?: boolean
}) {
  // TODO: Implement
  // 4	CellDefaultAccessory	accessory

  const label = protoProps.label_text?.translated_value ?? ''
  const body = protoProps.body_text?.translated_value
  const disabled = protoProps.disabled
  const icon = protoProps.icon
  const accessory = protoProps.accessory
  const accessoryLeadingImage = protoProps.accessory_leading_image

  const labelIcon = protoProps.label_icon ? (
    <TintedArcadeCellIcon
      tintedIcon={{
        icon: protoProps.label_icon,
        icon_tint_color: protoProps.label_icon.tint_color,
      }}
      isBackgroundRequired={false}
    />
  ) : undefined

  const asset = icon ? getAssetIcon({ protoProps: icon }) : undefined

  if (protoProps.body_icon) {
    // If you run into this error you'll need to add support for the body_icon. It wasn't originally
    // supported for time reasons -- the first use case of this component didn't need it.
    logger.warn(new Error('ArcadeCellFromProto: body_icon is not yet supported'))
  }

  if (protoProps.is_badged) {
    // If you run into this error you'll need to add support for is_badged. It wasn't originally
    // supported for time reasons -- the first use case of this component didn't need it.
    logger.warn(new Error('ArcadeCellFromProto: is_badged is not yet supported'))
  }

  if (accessory?.labeled_push) {
    return (
      <InteractiveCell
        label={label}
        disabled={disabled}
        cellProps={{
          asset,
          body,
          accessory: (
            <ArcadeAccessoryLabeledPushFromProto
              accessoryLeadingImage={accessoryLeadingImage}
              protoProps={accessory.labeled_push}
              disabled={disabled}
            />
          ),
        }}
        onClick={onClick}
      />
    )
  } else if (accessory?.push) {
    return <PushCell label={label} disabled={disabled} cellProps={{ asset, body }} onClick={onClick} />
  } else if (accessory?.label) {
    return (
      <BaseCell
        asset={asset}
        label={label}
        body={body}
        disabled={disabled}
        accessory={
          <ArcadeCellLabelAccessoryWrapper accessoryLeadingImage={accessoryLeadingImage}>
            <Text typography="bodySmall" textWrap="nowrap">
              {accessory.label?.translated_value}
            </Text>
          </ArcadeCellLabelAccessoryWrapper>
        }
        accessoryAlignment="center"
      />
    )
  } else if (accessory?.radio) {
    return (
      <RadioCell
        label={label}
        disabled={disabled}
        cellProps={{ asset, body, labelIcon, ...(labelIcon && { labelProps: { labelOverflow: 'ellipsis' } }) }}
        defaultChecked={initialSelection}
        onClick={onClick}
        {...(selectionGroupId && { name: selectionGroupId })}
      />
    )
  } else if (accessory?.checkbox) {
    return (
      <CheckboxCell
        label={label}
        disabled={disabled}
        cellProps={{ asset, body, labelIcon, ...(labelIcon && { labelProps: { labelOverflow: 'ellipsis' } }) }}
        onClick={onClick}
        defaultChecked={initialSelection}
      />
    )
  } else if (accessory) {
    // If you run into this error you'll need to add support for the missing accessory. It wasn't originally
    // supported for time reasons -- the first use case of this component didn't need it.
    logger.warn(
      new Error(
        'ArcadeCellFromProto: only labeled-push, push, radio, checkbox, or label accessories are currently supported'
      )
    )

    // Since we don't support any other accessory types yet, just return a base cell.
    return <BaseCell asset={asset} label={label} body={body} disabled={disabled} />
  } else {
    return <BaseCell asset={asset} label={label} body={body} disabled={disabled} />
  }
}

export default ArcadeCellFromProto
